import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const configured = Boolean(url && anonKey && !url.includes('YOUR_PROJECT'));
const STAY_SIGNED_IN_KEY = 'calendar-guardian-stay-signed-in-v1';
const shouldStaySignedIn = () => localStorage.getItem(STAY_SIGNED_IN_KEY) !== 'false';
const sessionStorageAdapter = {
  getItem(key) { return (shouldStaySignedIn() ? localStorage : sessionStorage).getItem(key); },
  setItem(key, value) { (shouldStaySignedIn() ? localStorage : sessionStorage).setItem(key, value); },
  removeItem(key) { localStorage.removeItem(key); sessionStorage.removeItem(key); },
};
const supabase = configured ? createClient(url, anonKey, {
  auth: { storage: sessionStorageAdapter, persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
}) : null;

const requireCloud = () => {
  if (!supabase) throw new Error('Cloud services are not configured yet. Demo mode remains available.');
  return supabase;
};

// Most phone photos are larger than the private avatar bucket's 5 MB limit.
// Prepare only oversized images so ordinary uploads keep their original file.
const prepareAvatarFile = async file => {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Choose a PNG, JPEG, or WebP image.');
  if (file.size <= 4.5 * 1024 * 1024) return file;
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1800 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  let quality = .9, blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
  while (blob && blob.size > 4.5 * 1024 * 1024 && quality > .5) { quality -= .1; blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality)); }
  if (!blob || blob.size > 5 * 1024 * 1024) throw new Error('That image is too large to prepare. Choose a smaller photo.');
  return new File([blob], 'profile.jpg', { type: 'image/jpeg' });
};

export const guardianCloud = {
  configured,
  supabase,
  staysSignedIn: shouldStaySignedIn,
  setStaySignedIn(enabled) { localStorage.setItem(STAY_SIGNED_IN_KEY, enabled ? 'true' : 'false'); },
  async signUp({ email, password, name, iconChoice }) {
    const { data, error } = await requireCloud().auth.signUp({
      email,
      password,
      options: { data: { display_name: name, icon_choice: iconChoice } },
    });
    if (error) throw error;
    return data;
  },
  async signIn({ email, password }) {
    const { data, error } = await requireCloud().auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },
  async signInWithGoogle() {
    // Supabase appends the OAuth credentials as a URL fragment.  Adding our
    // own `#settings` first creates `#settings#access_token=…`, which the
    // client cannot parse.  Return to the app root so it can consume the
    // credentials before normal navigation resumes.
    const redirectTo = `${location.origin}/`;
    const { data, error } = await requireCloud().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, skipBrowserRedirect: true, queryParams: { prompt: 'select_account' } },
    });
    if (error) throw error;
    if (!data?.url) throw new Error('Google sign-in could not be started.');
    return data.url;
  },
  async finishOAuthRedirect() {
    if (!supabase) return null;
    const callback = new URL(location.href);
    const code = callback.searchParams.get('code');
    if (!code) return null;
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    // Do not leave the one-use authorization code in the address bar or let a
    // refresh try to exchange it a second time.
    callback.searchParams.delete('code');
    history.replaceState({}, '', `${callback.pathname}${callback.search}${callback.hash}`);
    return data.session;
  },
  async signOut() {
    const { error } = await requireCloud().auth.signOut();
    if (error) throw error;
  },
  async requestPasswordReset(email) {
    const redirectTo = `${location.origin}/`;
    const { error } = await requireCloud().auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  },
  async updatePassword(password) {
    const { data, error } = await requireCloud().auth.updateUser({ password });
    if (error) throw error;
    return data;
  },
  async session() {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data.session;
  },
  // getSession only reads the browser cache.  Confirm the session with
  // Supabase before treating a saved profile as signed in, so a remotely
  // deleted account cannot remain visible after a refresh.
  async verifiedSession() {
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    const { data: { user }, error } = await supabase.auth.getUser(session.access_token);
    if (error || !user) {
      await supabase.auth.signOut({ scope: 'local' });
      return null;
    }
    return { ...session, user };
  },
  async saveItem(item) {
    const { data, error } = await requireCloud().from('planner_items').upsert(item).select().single();
    if (error) throw error;
    return data;
  },
  async savePreferences(preferences) {
    const { data: { user }, error: userError } = await requireCloud().auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const { data, error } = await requireCloud().from('user_preferences')
      .upsert({ user_id: user.id, preferences, updated_at: new Date().toISOString() })
      .select().single();
    if (error) throw error;
    return data;
  },
  async loadPreferences() {
    const { data, error } = await requireCloud().from('user_preferences').select('preferences').maybeSingle();
    if (error) throw error;
    return data?.preferences || null;
  },
  async saveSnapshot(snapshot) {
    const { data: { user }, error: userError } = await requireCloud().auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const { data, error } = await requireCloud().from('planner_snapshots')
      .upsert({ user_id: user.id, snapshot, updated_at: new Date().toISOString() })
      .select().single();
    if (error) throw error;
    return data;
  },
  async loadSnapshot() {
    const { data, error } = await requireCloud().from('planner_snapshots').select('snapshot,updated_at').maybeSingle();
    if (error) throw error;
    return data || null;
  },
  async uploadAvatar(file) {
    const { data: { user }, error: userError } = await requireCloud().auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    file = await prepareAvatarFile(file);
    const extension = file.type.split('/')[1].replace('jpeg', 'jpg');
    const path = `${user.id}/profile.${extension}`;
    const { error } = await requireCloud().storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type });
    if (error) throw error;
    return path;
  },
  async loadProfile() {
    const client = requireCloud();
    const { data: { user }, error: userError } = await client.auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const { data: profile, error } = await client.from('profiles').select('display_name,icon_choice,avatar_path,avatar_crop').maybeSingle();
    if (error) throw error;
    let avatar = '';
    if (profile?.avatar_path) {
      const { data, error: urlError } = await client.storage.from('avatars').createSignedUrl(profile.avatar_path, 60 * 60 * 24 * 30);
      if (urlError) throw urlError;
      avatar = data?.signedUrl || '';
    }
    // The database uses "local" for a stored image; the UI uses "custom".
    // Normalize at the boundary so a saved photo survives every reload.
    const storedChoice = profile?.icon_choice || user.user_metadata?.icon_choice || 'female';
    return { name: profile?.display_name || user.user_metadata?.display_name || user.email?.split('@')[0] || 'Guardian user', iconChoice: storedChoice === 'local' ? 'custom' : storedChoice, avatar, avatarCrop: profile?.avatar_crop || { x: 50, y: 50, zoom: 1 } };
  },
  async saveProfileAvatar(file, crop = { x: 50, y: 50, zoom: 1 }) {
    const client = requireCloud();
    const { data: { user }, error: userError } = await client.auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const path = await this.uploadAvatar(file);
    // `profiles.icon_choice` is constrained to the same values the UI exposes.
    // Persisting "local" here made a selected photo fail after it had already
    // been uploaded to storage, leaving the visible default portrait in place.
    const { error } = await client.from('profiles').update({ avatar_path: path, avatar_crop: crop, icon_choice: 'custom', updated_at: new Date().toISOString() }).eq('id', user.id);
    if (error) throw error;
    await client.auth.updateUser({ data: { icon_choice: 'custom' } });
    const { data, error: urlError } = await client.storage.from('avatars').createSignedUrl(path, 60 * 60 * 24 * 30);
    if (urlError) throw urlError;
    return { avatar: data?.signedUrl || '', avatarCrop: crop };
  },
  async saveAvatarCrop(crop) {
    const client = requireCloud();
    const { data: { user }, error: userError } = await client.auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const { error } = await client.from('profiles').update({ avatar_crop: crop, updated_at: new Date().toISOString() }).eq('id', user.id);
    if (error) throw error;
    return crop;
  },
  async saveProfileIconChoice(iconChoice) {
    const client = requireCloud();
    const { data: { user }, error: userError } = await client.auth.getUser();
    if (userError || !user) throw userError || new Error('Sign in first.');
    const databaseChoice = iconChoice === 'custom' ? 'local' : iconChoice;
    const { error } = await client.from('profiles').update({ icon_choice: databaseChoice, updated_at: new Date().toISOString() }).eq('id', user.id);
    if (error) throw error;
    await client.auth.updateUser({ data: { icon_choice: iconChoice } });
  },
  async deleteAccount() {
    const { data, error } = await requireCloud().functions.invoke('delete-account');
    if (error) throw error;
    return data;
  },
  async listItems(rangeStart, rangeEnd) {
    // Supabase returns at most 1,000 rows per request. Planner history and a
    // year of recurring events routinely exceed that, so load the full range.
    const client = requireCloud(), records = [];
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await client.from('planner_items').select('*')
        .gte('starts_at', rangeStart).lt('starts_at', rangeEnd)
        .order('starts_at').range(offset, offset + 999);
      if (error) throw error;
      records.push(...(data || []));
      if (!data || data.length < 1000) return records;
    }
  },
  async listCalendarConnections() {
    const { data, error } = await requireCloud().from('calendar_connections')
      .select('id, provider, provider_account_id, label, mode, status, settings, last_synced_at, created_at')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },
  async updateCalendarConnectionSettings(id, settings) {
    const { data, error } = await requireCloud().from('calendar_connections')
      .update({ settings }).eq('id', id).select('id, provider, provider_account_id, label, mode, status, settings, last_synced_at, created_at').single();
    if (error) throw error;
    return data;
  },
  async disconnectCalendarConnection(id) {
    const { error } = await requireCloud().from('calendar_connections').delete().eq('id', id);
    if (error) throw error;
  },
  async syncCalendarConnection(connectionId) {
    const client = requireCloud();
    const { data, error } = await client.functions.invoke('calendar-sync', { body: { connectionId } });
    if (error) {
      let message = error.message;
      try { message = (await error.context?.json())?.error || message; } catch {}
      throw new Error(message);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async pushPlannerItem(plannerItemId, remove = false) {
    const { data, error } = await requireCloud().functions.invoke('calendar-push', { body: { plannerItemId, remove } });
    if (error) { let message = error.message; try { message = (await error.context?.json())?.error || message; } catch {} throw new Error(message); }
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async syncPlannerToGoogle() {
    const { data, error } = await requireCloud().functions.invoke('calendar-push', { body: { syncAll: true } });
    if (error) { let message = error.message; try { message = (await error.context?.json())?.error || message; } catch {} throw new Error(message); }
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async clearCalendars() {
    const { data, error } = await requireCloud().functions.invoke('calendar-clear', { body: { confirm: true } });
    if (error) { let message = error.message; try { message = (await error.context?.json())?.error || message; } catch {} throw new Error(message); }
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async notificationConfig() {
    const { data, error } = await requireCloud().functions.invoke('notifications-dispatch', { body: { action: 'config' } });
    if (error) throw error;
    if (!data?.publicKey) throw new Error(data?.error || 'App notification setup is unavailable.');
    return data;
  },
  async subscribeToNotifications(subscription) {
    const { data, error } = await requireCloud().functions.invoke('notifications-dispatch', { body: { action: 'subscribe', subscription } });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async sendNotificationTest() {
    const { data, error } = await requireCloud().functions.invoke('notifications-dispatch', { body: { action: 'test' } });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },
  async beginCalendarConnection(provider) {
    const client = requireCloud();
    const { data: { user }, error: sessionError } = await client.auth.getUser();
    if (sessionError || !user) throw sessionError || new Error('Sign in to your secure Calendar Guardian account before connecting Google.');
    const { data, error } = await client.functions.invoke('calendar-oauth-start', { body: { provider } });
    if (error) throw error;
    if (!data?.authorizationUrl) throw new Error('Google authorization did not start. No calendar was added.');
    location.assign(data.authorizationUrl);
  },
};

window.guardianCloud = guardianCloud;

if ('serviceWorker' in navigator) {
  addEventListener('load', () => navigator.serviceWorker.register('/service-worker.js'));
}
